/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { css } from "@panda/css";
import { Box } from "@panda/jsx";
import { useDirectoryContext } from "../core/Directory.context";
import type { FilterDef } from "../core/Directory.types";
import { DirectoryChipRow } from "./DirectoryChipRow.element";
import { DirectoryComboBox } from "./DirectoryComboBox.element";
import { DirectorySearchInput } from "./DirectorySearchInput.element";
import { DirectorySelect } from "./DirectorySelect.element";
import { DirectoryTabs } from "./DirectoryTabs.element";

/* -------------------------------------------------------------------------- *
 * Layout
 *
 * Figma calls for: chips / tabs on the LEFT (consumes most width, wraps when
 * needed), search / combobox / select inputs on the RIGHT (fixed narrower
 * width). Same row on md+, stacked on mobile with the search input ON TOP
 * (mobile users see search first, expand chips below).
 * -------------------------------------------------------------------------- */

const outerRowStyles = css({
	display: "flex",
	width: "full",
	// Generous 80px gap on desktop separates the chip group from the search
	// input visually; tighter 24px on mobile where the row is stacked.
	gap: { base: "eds.dimension.24", md: "eds.dimension.80" },
	alignItems: { base: "stretch", md: "flex-start" },
	// Mobile stacks chips above the search input (matches DOM order).
	flexDirection: { base: "column", md: "row" },
});

const groupRowStyles = css({
	display: "flex",
	flexDirection: "column",
	gap: "eds.dimension.16",
	// LEFT side takes whatever space the chips need, wrapping internally.
	flex: { base: "0 0 auto", md: "1 1 auto" },
	minWidth: "0",
});

const inputRowStyles = css({
	display: "flex",
	flexDirection: "column",
	gap: "eds.dimension.12",
	// RIGHT side: 300px on desktop — gives the placeholder copy room to
	// breathe while still feeling like a focused side-panel next to the
	// (wide-wrapping) chip group.
	flex: { base: "0 0 auto", md: "0 0 300px" },
	width: { base: "full", md: "300px" },
});

/**
 * Renders the filter controls described by `config.filters`.
 *
 * Categorization:
 *   - LEFT group (chips/tabs/tabsMulti): pill-style controls that wrap and
 *     take whatever width they need.
 *   - RIGHT inputs (search/combobox/select): fixed-width input controls.
 *
 * Callers that want a different layout can skip `<Directory.Filters />` and
 * compose individual filter atoms via `useDirectoryContext()`.
 */
export function DirectoryFilters(): React.JSX.Element | null {
	const { config, result } = useDirectoryContext();
	const filters = config.filters;
	if (filters.length === 0) return null;

	const leftGroup: FilterDef<unknown>[] = [];
	const rightInputs: FilterDef<unknown>[] = [];

	for (const f of filters) {
		if (f.kind === "chips" || f.kind === "tabs" || f.kind === "tabsMulti") {
			leftGroup.push(f);
		} else {
			rightInputs.push(f);
		}
	}

	const renderFilter = (filter: FilterDef<unknown>): React.ReactNode => {
		switch (filter.kind) {
			case "search":
				return (
					<DirectorySearchInput
						key={filter.paramKey}
						value={result.state.searchQueries[filter.paramKey] ?? ""}
						onChange={(v) => result.setSearchQuery(filter.paramKey, v)}
						placeholder={filter.placeholder ?? config.searchPlaceholderLabel}
					/>
				);
			case "tabs":
			case "tabsMulti":
				return (
					<DirectoryTabs
						key={filter.paramKey}
						options={filter.options}
						value={result.state.singleValueFilters[filter.paramKey] ?? ""}
						onChange={(v) => result.setSingleValueFilter(filter.paramKey, v)}
						label={config.filterGroupLabel?.(filter.label ?? "") ?? filter.label}
					/>
				);
			case "select":
				return (
					<Box key={filter.paramKey} minWidth="220px">
						<DirectorySelect
							options={filter.options}
							value={result.state.singleValueFilters[filter.paramKey] ?? ""}
							onChange={(v) => result.setSingleValueFilter(filter.paramKey, v)}
							label={filter.label}
							placeholder={filter.placeholder}
						/>
					</Box>
				);
			case "chips":
				return (
					<DirectoryChipRow
						key={filter.paramKey}
						options={filter.options}
						values={result.state.multiValueFilters[filter.paramKey] ?? []}
						onToggle={(v) => result.toggleMultiValueFilter(filter.paramKey, v)}
						label={config.filterGroupLabel?.(filter.label ?? "") ?? filter.label}
					/>
				);
			case "combobox":
				return (
					<DirectoryComboBox
						key={filter.paramKey}
						options={filter.options}
						values={result.state.multiValueFilters[filter.paramKey] ?? []}
						onChange={(vs) => {
							const current = result.state.multiValueFilters[filter.paramKey] ?? [];
							const added = vs.filter((v) => !current.includes(v));
							const removed = current.filter((v) => !vs.includes(v));
							[...added, ...removed].forEach((v) => result.toggleMultiValueFilter(filter.paramKey, v));
						}}
						label={filter.label}
						ariaLabel={filter.ariaLabel}
						placeholder={filter.placeholder}
					/>
				);
		}
	};

	// `role="search"` (a landmark) tells AT users this whole region is the
	// directory's filter chrome — rotor / quick-nav users get a clear anchor
	// for "where the filters live" vs the result grid below.
	return (
		<Box role="search" aria-label={config.filtersRegionLabel ?? "Filter directory"} className={outerRowStyles}>
			{leftGroup.length > 0 ? <Box className={groupRowStyles}>{leftGroup.map(renderFilter)}</Box> : null}
			{rightInputs.length > 0 ? <Box className={inputRowStyles}>{rightInputs.map(renderFilter)}</Box> : null}
		</Box>
	);
}
